import React, { useEffect, useState } from 'react';
import { OwnerSelectResponseDTO } from '../../../models/user/user.response';
import { userService } from '../../../services/userService';

import { CloseGreyIcon } from '../../../assets/icons';
import { UserType } from '../../../models/base/UserType';
import { localService } from '../../../services/localService';
import { validateEmail } from '../../../utils/validators';
import { useNotification } from '../../toster-notification/NotificationContext';
import { ApiResponse } from '../../../models/base/ApiResponse';
import { nameToSlug } from '../../../utils/slugService';
import { SelectCityDataDTO } from '../../../models/city/city.response';
import Loader from '../../shared/Loader';
import { cityService } from '../../../services/cityService';
import { SelectLocationAreaDTO } from '../../../models/locationArea/locationArea.response';
import { locationAreaService } from '../../../services/locationAreaService';
import AddLocalsView from './AddLocalsView';
import { TagInfoDTO } from '../../../models/tag/tag.response';
import { tagService } from '../../../services/tagService';
import { TagType } from '../../../models/base/TagType';
import LocalWorkingHoursView from './LocalWorkingHoursView';
import SeoMetadataView from './SeoMetadataView';

interface EditLocalViewProps {
    id: string;
    onClose: () => void;
}

const EditLocalView: React.FC<EditLocalViewProps> = ({ id, onClose }) => {

    const [isWorkingHoursViewOpen, setIsWorkingHoursViewOpen] = useState<boolean>(false);
    const [isSeoMetadataViewOpen, setIsSeoMetadataViewOpen] = useState<boolean>(false);

    const [owners, setOwners] = useState<OwnerSelectResponseDTO[]>([]);
    const [cities, setCities] = useState<SelectCityDataDTO[]>([]);
    const [selectedCityId, setSelectedCityId] = useState<string>("");
    const [locationAreas, setLocationAreas] = useState<SelectLocationAreaDTO[]>([]);
    const [selectedLocationAreaIds, setSelectedLocationAreaIds] = useState<string[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

    const [localName, setLocalName] = useState<string>("");
    const [localError, setLocalError] = useState<string>("");
    const [ownerId, setOwnerId] = useState<string>("");
    const [ownerError, setOwnerError] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [phoneError, setPhoneError] = useState<string>("");
    const [slug, setSlug] = useState<string>("");
    const [slugError, setSlugError] = useState<string>("");
    const [cityError, setCityError] = useState<string>("");
    const [locationAreaError, setLocationAreaError] = useState<string>("");
    const [tags, setTags] = useState<TagInfoDTO[]>([]);
    const [tagError, setTagError] = useState<string>("");
    const [googleRating, setGoogleRating] = useState<number>(0);
    const [googleReviewCount, setGoogleReviewCount] = useState<number>(0);
    const [seoContentHtml, setSeoContentHtml] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();

    useEffect(() => {
        fetchOwners();
        fetchCities();
        fetchLocalInfo();
        fetchTags();
    }, []);

    const fetchOwners = async () => {
        setLoading(true);
        try {
            const response = await userService.getSelect(UserType.Admin - 1);

            if (response.success && response.data) {
                setOwners(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch owners:", error);
        } finally {
            setLoading(false);
        }
    }

    const fetchCities = async () => {
        setLoading(true);
        try {
            const response = await cityService.GetForSelect();

            if (response.success && response.data) {
                setCities(response.data);
            } else {
                notify("error", "Error Fetching Cities", "An error occurred while fetching cities.");
            }
        } catch (error) {
            console.error("Error fetching cities:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLocationAreas = async (cityId: string) => {
        setLoading(true);
        try {
            const response: ApiResponse<SelectLocationAreaDTO[]> = await locationAreaService.GetForSelect(cityId);

            if (response.success && response.data) {
                setLocationAreas(response.data);
            } else {
                notify("error", "Error Fetching Location Areas", "An error occurred while fetching location areas.");
            }
        } catch (error) {
            notify("error", "Error Fetching Location Areas", "An error occurred while fetching location areas.");
        } finally {
            setLoading(false);
        }
    };

    const fetchTags = async () => {
        setLoading(true);
        try {
            const response = await tagService.getAll();

            if (response.success && response.data) {
                setTags(response.data);
            } else {
                notify("error", "Error Fetching Tags", "An error occurred while fetching tags.");
            }
        } catch (error) {
            notify("error", "Error Fetching Tags", "An error occurred while fetching tags.");
        } finally {
            setLoading(false);
        }
    };

    const fetchLocalInfo = async () => {
        setLoading(true);
        try {
            const response = await localService.getInfo(id);

            if (response.success && response.data) {
                const localInfo = response.data;
                setLocalName(localInfo.name);
                setSlug(localInfo.slug);
                setOwnerId(localInfo.ownerId);
                setEmail(localInfo.email);
                setPhone(localInfo.phone);
                setSelectedCityId(localInfo.cityId);
                setSelectedLocationAreaIds(localInfo.locationAreaIds);
                setSelectedTagIds(localInfo.tagsIds);
                setGoogleRating(localInfo.googleRating);
                setGoogleReviewCount(localInfo.googleReviewCount);
                fetchLocationAreas(localInfo.cityId);
                setSeoContentHtml(localInfo.seoContentHtml);
            }
        } catch (error) {
            console.error("Failed to fetch local info:", error);
        } finally {
            setLoading(false);
        }
    };


    const handleUpdateLocal = async () => {
        setLoading(true);

        if (ownerId === "") {
            setOwnerError("Owner is required");
            setLoading(false);
            return;
        }

        // let emailValidateError = validateEmail(email);
        // if (emailValidateError) {
        //     setEmailError(emailValidateError);
        //     setLoading(false);
        //     return;
        // }

        try {
            const response = await localService.update(id, {
                name: localName,
                ownerId: ownerId,
                email: email,
                phone: phone,
                cityId: selectedCityId,
                locationAreaIds: selectedLocationAreaIds,
                tagsIds: selectedTagIds,
                googleRating: googleRating,
                googleReviewCount: googleReviewCount,
                seoContentHtml: seoContentHtml
            });

            notify("success", response.message || "Local updated successfully.");
            onClose();


        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to update local.");
            }
            console.error("Failed to update local:", error);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {isWorkingHoursViewOpen && (
                <LocalWorkingHoursView
                    localId={id}
                    onClose={() => setIsWorkingHoursViewOpen(false)}
                />
            )}

            {isSeoMetadataViewOpen && (
                <SeoMetadataView
                    localId={id}
                    onClose={() => setIsSeoMetadataViewOpen(false)}
                />
            )}

            <section className="modal-section">
                <div className="w-layout-blockcontainer container w-container">
                    <div className="modal-wrapper">
                        <div className="modal-div">
                            <div className="modal-header-div">
                                <div>
                                    <div className="modal-header">Edit Local</div>
                                    <div className="description-text">Update details for the local</div>
                                </div>
                                <div className="modal-button-div" onClick={onClose}>
                                    <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                                </div>
                            </div>
                            <div className="modal-form-block w-form">
                                <form className="modal-form">

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Local Name</label>
                                        <input className="form-input grey w-input" placeholder="Enter local name" type="text"
                                            value={localName}
                                            onChange={(e) => {
                                                setLocalName(e.target.value)
                                            }}
                                        />
                                        <div className='error-text'>{localError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Local Slug</label>
                                        <input className="form-input grey w-input" placeholder="Enter local slug" type="text"
                                            value={slug}
                                            disabled
                                        />
                                        <div className='error-text'>{slugError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Owner *</label>
                                        <select className="form-select strech w-select"
                                            value={ownerId}
                                            onChange={(e) => setOwnerId(e.target.value)}
                                        >
                                            <option value="" disabled>Select owner</option>

                                            {owners.map((owner) => (
                                                <option key={owner.id} value={owner.id}>{owner.fullName}</option>
                                            ))}

                                        </select>
                                        <div className='error-text'>{ownerError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Email*</label>
                                        <input className="form-input grey w-input" placeholder="Enter email" type="email" name='email'
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                        <div className='error-text'>{emailError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Phone</label>
                                        <input className="form-input grey w-input" placeholder="Enter phone number" type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                        />
                                        <div className='error-text'>{phoneError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Ocena lokala</label>
                                        <input className="form-input grey w-input" placeholder="Enter google rating" type="number"
                                            value={googleRating}
                                            onChange={(e) => setGoogleRating(parseFloat(e.target.value) || 0)}
                                        />
                                        {/* <div className='error-text'>{googleRatingError}</div> */}
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Broj recenzija</label>
                                        <input className="form-input grey w-input" placeholder="Enter google review count" type="number"
                                            value={googleReviewCount}
                                            onChange={(e) => setGoogleReviewCount(parseInt(e.target.value) || 0)}
                                        />
                                        {/* <div className='error-text'>{googleReviewCountError}</div> */}
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Html sadržaj za Gastro Radar</label>
                                        <textarea className="form-textarea grey w-input" placeholder="Enter seo content html"
                                            value={seoContentHtml}
                                            onChange={(e) => setSeoContentHtml(e.target.value)}
                                        />
                                        {/* <div className='error-text'>{seoContentHtmlError}</div> */}
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">City *</label>
                                        <select className="form-select strech w-select"
                                            value={selectedCityId}
                                            onChange={(e) => {
                                                setSelectedCityId(e.target.value)
                                                fetchLocationAreas(e.target.value);
                                            }}
                                        >
                                            <option value="">Select City</option>

                                            {cities.map((city) => (
                                                <option key={city.id} value={city.id}>{city.name}</option>
                                            ))}

                                        </select>
                                        {/* <div className='error-text'>{cityError}</div> */}
                                    </div>

                                    <div className="modal-form-div">
                                        <div className="div-block-30">
                                            <label className="modal-form-label">Oblast Grada</label>
                                        </div>
                                        <div className="div-block-31">
                                            {locationAreas.map((locationArea) => (
                                                <div key={locationArea.id} className={`small-box-div ${selectedLocationAreaIds.includes(locationArea.id) ? 'dark-green-bg' : 'grey-bg'} pointer`}
                                                    onClick={() => {
                                                        if (selectedLocationAreaIds.includes(locationArea.id)) {
                                                            setSelectedLocationAreaIds(selectedLocationAreaIds.filter(id => id !== locationArea.id));
                                                        } else {
                                                            setSelectedLocationAreaIds([...selectedLocationAreaIds, locationArea.id]);
                                                        }
                                                    }}
                                                >
                                                    <div>{locationArea.name}</div>
                                                </div>
                                            ))}

                                        </div>
                                    </div>

                                    <div className="modal-form-div">
                                        <div className="div-block-30">
                                            <label className="modal-form-label">Tagovi</label>
                                        </div>

                                        {tags.find(tag => tag.type === TagType.LocalType) && (
                                            <div className="div-block-31">
                                                {tags.filter(tag => tag.type === TagType.LocalType).map((tag) => (
                                                    <div key={tag.id} className={`small-box-div ${selectedTagIds.includes(tag.id) ? 'light-skyblue-bg' : 'grey-bg'} pointer`}
                                                        onClick={() => {
                                                            if (selectedTagIds.includes(tag.id)) {
                                                                setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                                                            } else {
                                                                setSelectedTagIds([...selectedTagIds, tag.id]);
                                                            }
                                                        }}
                                                    >
                                                        <div>{tag.name}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {tags.find(tag => tag.type === TagType.Feature) && (
                                            <div className="div-block-31">
                                                {tags.filter(tag => tag.type === TagType.Feature).map((tag) => (
                                                    <div key={tag.id} className={`small-box-div ${selectedTagIds.includes(tag.id) ? 'light-green-bg' : 'grey-bg'} pointer`}
                                                        onClick={() => {
                                                            if (selectedTagIds.includes(tag.id)) {
                                                                setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                                                            } else {
                                                                setSelectedTagIds([...selectedTagIds, tag.id]);
                                                            }
                                                        }}
                                                    >
                                                        <div>{tag.name}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {tags.find(tag => tag.type === TagType.Ambience) && (
                                            <div className="div-block-31">
                                                {tags.filter(tag => tag.type === TagType.Ambience).map((tag) => (
                                                    <div key={tag.id} className={`small-box-div ${selectedTagIds.includes(tag.id) ? 'light-purple-bg' : 'grey-bg'} pointer`}
                                                        onClick={() => {
                                                            if (selectedTagIds.includes(tag.id)) {
                                                                setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                                                            } else {
                                                                setSelectedTagIds([...selectedTagIds, tag.id]);
                                                            }
                                                        }}
                                                    >
                                                        <div>{tag.name}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                    </div>

                                    <div className="modal-form-div f-horizontal">
                                        <div className="div-block-30">
                                            <label className="modal-form-label">Radno vreme</label>
                                        </div>
                                        <div className="button-primary small grey-bg" onClick={() => setIsWorkingHoursViewOpen(true)}>
                                            <div>Upravljaj radnim vremenom</div>
                                        </div>
                                    </div>

                                    <div className="modal-form-div f-horizontal">
                                        <div className="div-block-30">
                                            <label className="modal-form-label">SeoMetadata</label>
                                        </div>
                                        <div className="button-primary small grey-bg" onClick={() => setIsSeoMetadataViewOpen(true)}>
                                            <div>Upravljaj SeoMetadata podacima</div>
                                        </div>
                                    </div>

                                </form>
                            </div>
                            <div className="modal-buttons">
                                <div className="button-secondary medium" onClick={onClose}>
                                    <div>Cancel</div>
                                </div>
                                <div className="button-primary medium" onClick={handleUpdateLocal}>
                                    <div>Update Local</div>
                                </div>
                            </div>
                        </div>
                    </div >
                </div >
            </section >
        </>
    );
};

export default EditLocalView;